import { useState } from "react";
import { Link } from "react-router-dom";
import { Image as ImageIcon, Loader2, ArrowUp, ArrowDown, Trash2, Clapperboard, Coins, Play } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { API } from "@/lib/api";

export const TRANSITIONS = [
  { value: "none", label: "Cut" },
  { value: "fade", label: "Fade" },
  { value: "slide", label: "Slide" },
  { value: "zoom", label: "Zoom" },
  { value: "dissolve", label: "Dissolve" },
];

function SceneRow({ scene, index, count, videoId, mediaToken, imgLoading, animLoading, motionCost, onSceneImage, onRequestAnimate, onPreview, onSceneLocal, onSceneSave, onMove, onDelete }) {
  const imgSrc = scene.has_image && scene.sid && mediaToken
    ? `${API}/videos/${videoId}/scene-image/${scene.sid}?token=${mediaToken}&v=${encodeURIComponent(scene.image_updated_at || "")}`
    : null;
  const clipUrl = scene.has_clip && scene.sid && mediaToken
    ? `${API}/videos/${videoId}/scene-clip/${scene.sid}?token=${mediaToken}&v=${encodeURIComponent(scene.clip_updated_at || "")}`
    : null;
  const animating = animLoading === index || scene.clip_status === "generating";
  return (
    <div className="border border-white/10 rounded-md bg-[#121212] p-4 flex gap-4" data-testid={`scene-${index}`}>
      <div className="w-28 shrink-0">
        <div
          className={`aspect-video rounded-sm overflow-hidden bg-[#0A0A0A] border border-white/10 flex items-center justify-center relative ${clipUrl ? "cursor-pointer group/thumb" : ""}`}
          onClick={clipUrl ? () => onPreview(clipUrl, index) : undefined}
          data-testid={`scene-thumb-${index}`}
        >
          {imgSrc ? (
            <>
              <img src={imgSrc} alt="" className="w-full h-full object-cover" data-testid={`scene-image-${index}`} />
              {clipUrl && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-80 group-hover/thumb:opacity-100 transition-opacity">
                  <span className="h-7 w-7 rounded-full bg-fuchsia-600/90 flex items-center justify-center">
                    <Play className="h-3.5 w-3.5 text-white" fill="white" />
                  </span>
                </span>
              )}
            </>
          ) : (
            <button
              onClick={() => onSceneImage(index)}
              disabled={imgLoading === index}
              data-testid={`scene-image-btn-${index}`}
              className="text-zinc-500 hover:text-blue-400 transition-colors flex flex-col items-center gap-1 text-[10px]"
            >
              {imgLoading === index ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
              {imgLoading === index ? "…" : "Add visual"}
            </button>
          )}
        </div>
        {clipUrl ? (
          <button
            onClick={() => onPreview(clipUrl, index)}
            data-testid={`scene-clip-link-${index}`}
            title="Preview this scene's motion clip"
            className="mt-1 w-full text-[10px] text-fuchsia-300 hover:text-fuchsia-200 transition-colors flex items-center justify-center gap-1"
          >
            <Clapperboard className="h-3 w-3" /> Motion ✓ · Preview
          </button>
        ) : animating ? (
          <p className="mt-1 text-[10px] text-zinc-500 flex items-center justify-center gap-1" data-testid={`scene-animating-${index}`}>
            <Loader2 className="h-3 w-3 animate-spin" /> Animating…
          </p>
        ) : scene.has_image ? (
          <button
            onClick={() => onRequestAnimate(index)}
            data-testid={`scene-animate-btn-${index}`}
            title="Turn this still into a true AI motion clip timed to this scene"
            className="mt-1 w-full text-[10px] text-zinc-500 hover:text-fuchsia-300 transition-colors flex items-center justify-center gap-1"
          >
            <Clapperboard className="h-3 w-3" /> Animate · {motionCost}cr
          </button>
        ) : null}
        {scene.clip_status === "failed" && !animating && (
          <p className="text-[9px] text-red-400 text-center" data-testid={`scene-clip-failed-${index}`}>failed — try again</p>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between mb-1">
          <p className="text-xs uppercase tracking-[0.15em] text-zinc-500">Scene {index + 1}</p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onMove(index, "up")}
              disabled={index === 0}
              data-testid={`scene-up-${index}`}
              className="h-7 w-7 flex items-center justify-center rounded border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => onMove(index, "down")}
              disabled={index === count - 1}
              data-testid={`scene-down-${index}`}
              className="h-7 w-7 flex items-center justify-center rounded border border-white/10 text-zinc-400 hover:text-white hover:bg-white/5 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => onDelete(index)}
              disabled={count <= 1}
              data-testid={`scene-delete-${index}`}
              className="h-7 w-7 flex items-center justify-center rounded border border-white/10 text-zinc-400 hover:text-red-400 hover:bg-red-500/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <p className="text-sm text-zinc-200 mb-2.5">{scene.narration}</p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-1">On-screen caption</label>
            <input
              value={scene.on_screen_text || ""}
              onChange={(e) => onSceneLocal(index, { on_screen_text: e.target.value })}
              onBlur={() => onSceneSave(index, { on_screen_text: scene.on_screen_text || "" })}
              data-testid={`scene-caption-${index}`}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2.5 py-1.5 text-sm text-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-1">Transition in</label>
            <select
              value={scene.transition || "fade"}
              onChange={(e) => onSceneSave(index, { transition: e.target.value })}
              data-testid={`scene-transition-${index}`}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {TRANSITIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.15em] text-zinc-600 mb-1">Duration</label>
            <select
              value={scene.duration || 0}
              onChange={(e) => onSceneSave(index, { duration: Number(e.target.value) })}
              data-testid={`scene-duration-${index}`}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={0}>Auto</option>
              {[2, 3, 4, 5, 6, 8, 10, 12, 15].map((s) => (
                <option key={s} value={s}>{s}s</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

function AnimateConfirmDialog({ confirm, quote, onConfirm, onClose }) {
  if (!confirm || !quote) return null;
  const isAll = confirm.type === "all";
  const eligible = quote.scenes.filter((s) => s.eligible);
  const shorts = eligible.filter((s) => s.clip_len === "6");
  const longs = eligible.filter((s) => s.clip_len === "10");
  const fullTotal = quote.total_cost;
  const bundled = isAll && quote.bundle_eligible;
  const cost = isAll ? (bundled ? quote.bundle_total : fullTotal) : (quote.scenes[confirm.index]?.cost ?? 25);
  const after = quote.credits - cost;
  const insufficient = after < 0;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-md" data-testid="animate-confirm-dialog">
        <DialogTitle className="font-head text-lg font-bold">
          {isAll ? `Animate ${eligible.length} scene${eligible.length > 1 ? "s" : ""}?` : `Animate scene ${confirm.index + 1}?`}
        </DialogTitle>
        <DialogDescription className="text-sm text-zinc-400">
          Each still becomes a true AI motion clip timed to its scene — longer scenes get longer clips.
        </DialogDescription>
        <div className="space-y-2 text-sm border border-white/10 rounded-md bg-[#0A0A0A] p-4">
          {isAll ? (
            <>
              {shorts.length > 0 && (
                <div className="flex justify-between text-zinc-400">
                  <span>{shorts.length} short scene{shorts.length > 1 ? "s" : ""} × 25cr</span>
                  <span>{shorts.length * 25}</span>
                </div>
              )}
              {longs.length > 0 && (
                <div className="flex justify-between text-zinc-400">
                  <span>{longs.length} long scene{longs.length > 1 ? "s" : ""} × 40cr</span>
                  <span>{longs.length * 40}</span>
                </div>
              )}
              {bundled && (
                <div className="flex justify-between text-emerald-400" data-testid="animate-bundle-discount">
                  <span>Subscriber bundle −{quote.bundle_discount_pct}%</span>
                  <span>−{fullTotal - quote.bundle_total}</span>
                </div>
              )}
            </>
          ) : (
            <div className="flex justify-between text-zinc-400">
              <span>{quote.scenes[confirm.index]?.clip_len === "10" ? "Long scene (10s clip)" : "Short scene (6s clip)"}</span>
              <span>{cost}</span>
            </div>
          )}
          <div className="flex justify-between font-semibold border-t border-white/10 pt-2">
            <span>Total</span>
            <span className="text-fuchsia-300" data-testid="animate-cost-total">{cost} credits</span>
          </div>
          <div className={`flex justify-between text-xs ${insufficient ? "text-red-400" : "text-zinc-500"}`} data-testid="animate-balance-after">
            <span>Balance after</span>
            <span>{after} credits</span>
          </div>
        </div>
        {!quote.is_subscriber && (
          <p className="text-xs text-zinc-500" data-testid="animate-upsell-note">
            {isAll && eligible.length >= 2
              ? `Tip: subscribers save ${quote.bundle_discount_pct}% on batch animations — and monthly plans have the cheapest credits.`
              : "Tip: monthly plans have the cheapest credits — Creator ($39/mo) includes 3,500 every month."}
          </p>
        )}
        <div className="flex items-center gap-3">
          <button
            onClick={onClose}
            data-testid="animate-cancel-btn"
            className="flex-1 py-2.5 rounded-md text-sm text-zinc-400 hover:text-white border border-white/10 hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          {insufficient ? (
            <Link
              to="/credits"
              data-testid="animate-get-credits-btn"
              className="flex-[2] py-2.5 rounded-md text-sm font-semibold bg-blue-600 hover:bg-blue-500 transition-colors text-center flex items-center justify-center gap-2"
            >
              <Coins className="h-4 w-4" /> Get credits
            </Link>
          ) : (
            <button
              onClick={onConfirm}
              data-testid="animate-confirm-btn"
              className="flex-[2] py-2.5 rounded-md text-sm font-semibold bg-fuchsia-600 hover:bg-fuchsia-500 transition-colors flex items-center justify-center gap-2"
            >
              <Clapperboard className="h-4 w-4" /> {isAll ? "Animate all" : "Animate"} · {cost}cr
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function SceneList({ scenes, hook, cta, videoId, mediaToken, imgLoading, animLoading, animAllLoading, quote, onSceneImage, onSceneAnimate, onAnimateAll, onSceneLocal, onSceneSave, onMove, onDelete }) {
  const [preview, setPreview] = useState(null); // {url, index}
  const [confirm, setConfirm] = useState(null); // {type:'one', index} | {type:'all'}
  const eligible = scenes.filter((s) => s.has_image && !s.has_clip && s.clip_status !== "generating").length;
  const costFor = (i) => quote?.scenes?.[i]?.cost ?? 25;
  const totalCost = quote?.bundle_total ?? quote?.total_cost ?? eligible * 25;

  const runConfirm = () => {
    const c = confirm;
    setConfirm(null);
    if (c?.type === "all") onAnimateAll();
    else if (c) onSceneAnimate(c.index);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-head text-lg font-semibold">Scene editor ({scenes.length})</h2>
        <div className="flex items-center gap-3">
          <span className="hidden sm:inline text-xs text-zinc-500">Edit captions · transitions · durations · reorder</span>
          {eligible > 0 && (
            <button
              onClick={() => setConfirm({ type: "all" })}
              disabled={animAllLoading}
              data-testid="animate-all-btn"
              title="Turn every still scene into a true AI motion clip timed to each scene"
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-fuchsia-500/40 bg-fuchsia-600/10 text-fuchsia-300 hover:bg-fuchsia-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {animAllLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clapperboard className="h-3.5 w-3.5" />}
              {animAllLoading ? "Animating all…" : `Animate all · ${totalCost}cr`}
              {!animAllLoading && quote?.bundle_eligible && (
                <span
                  data-testid="animate-bundle-badge"
                  className="ml-0.5 text-[9px] uppercase tracking-wider font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-500/40 rounded px-1.5 py-0.5"
                >
                  Save {quote.bundle_discount_pct}%
                </span>
              )}
            </button>
          )}
        </div>
      </div>
      {hook && (
        <div className="border border-blue-500/30 bg-blue-600/10 rounded-md p-4">
          <p className="text-xs uppercase tracking-[0.15em] text-blue-400 mb-1">Hook (first 3 seconds)</p>
          <p className="text-sm text-zinc-200">{hook}</p>
        </div>
      )}
      {scenes.map((s, i) => (
        <SceneRow
          key={s.sid || `scene-${i}`}
          scene={s}
          index={i}
          count={scenes.length}
          videoId={videoId}
          mediaToken={mediaToken}
          imgLoading={imgLoading}
          animLoading={animLoading}
          motionCost={costFor(i)}
          onSceneImage={onSceneImage}
          onRequestAnimate={(idx) => setConfirm({ type: "one", index: idx })}
          onPreview={(url, idx) => setPreview({ url, index: idx })}
          onSceneLocal={onSceneLocal}
          onSceneSave={onSceneSave}
          onMove={onMove}
          onDelete={onDelete}
        />
      ))}
      {cta && (
        <div className="border border-white/10 bg-[#121212] rounded-md p-4">
          <p className="text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1">Call to action</p>
          <p className="text-sm text-zinc-200">{cta}</p>
        </div>
      )}

      {/* Motion clip preview */}
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#121212] border-white/10 text-white sm:max-w-lg" data-testid="motion-preview-dialog">
          <DialogTitle className="font-head text-lg font-bold">
            Scene {(preview?.index ?? 0) + 1} — motion preview
          </DialogTitle>
          <DialogDescription className="text-sm text-zinc-500">
            This clip plays timed to the scene in your final video.
          </DialogDescription>
          {preview && (
            <video
              src={preview.url}
              controls
              autoPlay
              loop
              playsInline
              className="w-full rounded-md border border-white/10 bg-black"
              data-testid="motion-preview-video"
            />
          )}
        </DialogContent>
      </Dialog>

      <AnimateConfirmDialog confirm={confirm} quote={quote} onConfirm={runConfirm} onClose={() => setConfirm(null)} />
    </div>
  );
}
